import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Calendar, Clock, MapPin, AlertCircle } from 'lucide-react';
import { Input } from '../components/ContactForm/Input';

interface Event {
  id: number;
  title: string;
  date: string;
  time: string;
  location: string;
  description: string;
  imageUrl: string;
  timestamp: number;
  stripeLink: string;
}

interface FormData {
  firstName: string;
  lastName: string;
}

interface FormErrors {
  [key: string]: string;
}

const events: Event[] = [
  {
    id: 1,
    title: "Eve Ruggieri et Rachid Khimoune",
    date: "20 janvier 2025",
    time: "19:15",
    location: "Lieu sera précisé ultérieurement",
    description: "Une soirée exceptionnelle en compagnie d'Eve Ruggieri et de l'artiste Rachid Khimoune.",
    imageUrl: "https://zupimages.net/up/24/48/p6de.png",
    timestamp: new Date('2025-01-20T19:15:00').getTime(),
    stripeLink: "https://buy.stripe.com/bIYeX6glVaqC6sMaEG"
  }
];

export const Payment = () => {
  const { eventId } = useParams();
  const [isProcessing, setIsProcessing] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  
  const event = events.find(e => e.id === Number(eventId));

  const validateForm = () => {
    const newErrors: FormErrors = {};
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'Le prénom est requis';
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Le nom est requis';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!event || !validateForm()) return;
    
    setIsProcessing(true);
    
    try {
      // Redirect to Stripe payment page
      window.location.href = event.stripeLink;
    } catch (error) {
      console.error('Error:', error);
      alert('Une erreur est survenue. Veuillez réessayer.');
      setIsProcessing(false);
    }
  };

  if (!event) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="bg-red-50 text-red-600 p-4 rounded-lg">
          Événement non trouvé
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      <h1 className="text-3xl font-bold mb-8">Inscription à l'événement</h1>
      
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="p-6 border-b">
          <div className="flex items-start gap-6">
            <img
              src={event.imageUrl}
              alt={event.title}
              className="w-32 h-32 rounded-lg object-contain bg-gray-50"
            />
            <div>
              <h2 className="text-2xl font-semibold mb-4">{event.title}</h2>
              <div className="space-y-2 text-gray-600">
                <div className="flex items-center">
                  <Calendar className="h-5 w-5 mr-2" />
                  <span>{event.date}</span>
                </div>
                <div className="flex items-center">
                  <Clock className="h-5 w-5 mr-2" />
                  <span>{event.time}</span>
                </div>
                <div className="flex items-center">
                  <MapPin className="h-5 w-5 mr-2" />
                  <span>{event.location}</span>
                </div>
                <div className="flex items-center mt-2 text-amber-600">
                  <AlertCircle className="h-5 w-5 mr-2" />
                  <span>Réservation possible jusqu'au 31 décembre 2024</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <Input
              label="Prénom"
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              error={errors.firstName}
              placeholder="Votre prénom"
            />
            <Input
              label="Nom"
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              error={errors.lastName}
              placeholder="Votre nom"
            />
          </div>

          <button
            type="submit"
            disabled={isProcessing}
            className={`w-full py-3 px-6 rounded-lg text-white font-medium ${
              isProcessing 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-primary-600 hover:bg-primary-700'
            } transition-colors duration-200`}
          >
            {isProcessing ? 'Traitement en cours...' : 'Procéder au paiement'}
          </button>

          <div className="mt-6 text-sm text-gray-500 bg-gray-50 p-4 rounded-lg">
            <p className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              Pour une raison d'organisation, nous ne procédons pas aux remboursements après le 13 janvier en cas d'annulation.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};